import {
  PreScreeningQuestion,
  QuestionType,
  standardHeightQuestion,
  standardWeightQuestion,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  RESPIRATORY_ATTRIBUTES,
  CARDIOVASCULAR_ATTRIBUTES,
  MEASUREMENT_ATTRIBUTES,
  LIVER_KIDNEY_ATTRIBUTES,
  INFECTIOUS_DISEASE_ATTRIBUTES,
  GASTROINTESTINAL_ATTRIBUTES,
  CLINICAL_TRIAL_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Pre-screening questions for Presto COPD Study (HMIO variant)
 * Trial ID: VCOPDD7860C00006
 *
 * Inclusion criteria:
 * - Age 40 or older
 * - COPD diagnosis
 * - Smoking history assessment
 * - Willing to attend up to 7 visits over 7 months
 * - BMI between 18-45
 */
export const prestoCOPDhmioScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "age",
    type: QuestionType.RADIO,
    question: "What is your age?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "40_or_older",
        label: "40 or older",
      },
      {
        value: "39_or_younger",
        label: "39 or younger",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study is only available to participants who are 40 years of age or older.",
    attributeMapping: mappers.value({
      "40_or_older": [],
      "39_or_younger": [],
    }),
  },
  {
    id: "copd_diagnosis",
    type: QuestionType.RADIO,
    question: "Has a doctor or healthcare provider ever told you that you have COPD?",
    helpText: "Chronic bronchitis and emphysema are both types of chronic obstructive pulmonary disease (COPD)",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have been diagnosed with COPD"
      },
      {
        value: "no",
        label: "No, I have not been diagnosed with COPD",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study is only for people who have been diagnosed with COPD by a healthcare provider.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_COPD, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_COPD, booleanAttributeValue: false }],
    }),
  },
  {
    id: "copd_diagnosis_timing",
    type: QuestionType.RADIO,
    question: "Have you had COPD for 12 months (1 year) or longer?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have had COPD for 12 months or longer"
      },
      {
        value: "no",
        label: "No, I was diagnosed less than 12 months ago",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study requires participants who have had COPD for at least 12 months.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 22295, booleanAttributeValue: true }],
      no: [{ attributeDefinitionId: 22295, booleanAttributeValue: false }],
    }),
  },
  {
    id: "smoking_history",
    type: QuestionType.RADIO,
    question: "What is your cigarette smoking history?",
    helpText: "Please select the best answer. This study requires current or former smokers with >10 pack-years of smoking history.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "few_times",
        label: "I have only smoked a few times",
        disqualify: true,
      },
      { value: "heavy_current", label: "I am a heavy smoker and currently smoke a lot (1 pack per day or more)" },
      { value: "heavy_former", label: "I used to be a heavy smoker (1 pack per day or more for many years)" },
      {
        value: "rare_social",
        label: "I rarely smoke or only smoke in social settings",
        disqualify: true,
      },
      {
        value: "never_smoked",
        label: "I have never smoked cigarettes",
        disqualify: true,
      },
      {
        value: "not_sure",
        label: "Not sure",
        disqualify: true,
      },
    ],
    disqualifyMessage:
      "This study requires participants who are current or former heavy smokers with more than 10 pack-years of smoking history.",
    attributeMapping: mappers.value({
      few_times: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.IS_SMOKER, booleanAttributeValue: false },
        { attributeDefinitionId: 20561, booleanAttributeValue: true }, // is_pastsmoker
      ],
      heavy_current: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.IS_SMOKER, booleanAttributeValue: true },
      ],
      heavy_former: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.IS_SMOKER, booleanAttributeValue: false },
        { attributeDefinitionId: 20561, booleanAttributeValue: true }, // is_pastsmoker
      ],
      rare_social: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.IS_SMOKER, booleanAttributeValue: true },
      ],
      never_smoked: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.IS_SMOKER, booleanAttributeValue: false },
        { attributeDefinitionId: 20561, booleanAttributeValue: false }, // is_pastsmoker
      ],
      not_sure: [],
    }),
  },
  {
    id: "copd_symptoms",
    type: QuestionType.CHECKBOX,
    question: "Do you frequently suffer from one or more of the following COPD symptoms?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      { value: "difficulty_breathing", label: "Difficulty breathing" },
      { value: "wheezing", label: "Wheezing" },
      { value: "cough", label: "Cough" },
      { value: "chest_tightness", label: "Chest tightness" },
      { value: "excess_mucus", label: "Excess mucus or phlegm" },
      { value: "other_symptoms", label: "Other COPD symptoms" },
      {
        value: "rarely_symptoms",
        label: "I rarely experience any symptoms",
        disqualify: true
      },
      {
        value: "never_symptoms",
        label: "I never experience symptoms",
        disqualify: true
      },
    ],
    disqualifyMessage: "This study requires participants who frequently experience COPD symptoms.",
    attributeMapping: mappers.value({
      difficulty_breathing: [],
      wheezing: [],
      cough: [],
      chest_tightness: [],
      excess_mucus: [],
      other_symptoms: [],
      rarely_symptoms: [],
      never_symptoms: [],
    }),
  },
  {
    id: "severe_exacerbation",
    type: QuestionType.RADIO,
    question: "In the past 12 months, have you been hospitalised for COPD?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have been hospitalised for COPD"
      },
      {
        value: "no",
        label: "No, I have not been hospitalised for COPD"
      },
    ],
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_COPD_EXACERBATION_SEVERE_1_12_MONTHS, booleanAttributeValue: true },
      ],
      no: [
        { attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_COPD_EXACERBATION_SEVERE_1_12_MONTHS, booleanAttributeValue: false },
      ],
    }),
  },
  {
    id: "moderate_exacerbations",
    type: QuestionType.RADIO,
    question: "In the past 12 months, have you required medication prescribed by a doctor to treat a COPD flare-up (antibiotics or steroids)?",
    helpText: "A COPD flare-up is also known as a COPD exacerbation.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no",
        label: "No, I have not required prescribed medication",
        disqualify: (allAnswers: Record<string, string | string[] | number | null | undefined>) => {
          // Only disqualify if they answered "no" to hospitalization
          // If they had hospitalization, they pass regardless of medication
          return allAnswers.severe_exacerbation === "no";
        }
      },
      {
        value: "yes_once",
        label: "Yes, I have required prescribed medication once",
        disqualify: (allAnswers: Record<string, string | string[] | number | null | undefined>) => {
          // Only disqualify if they answered "no" to hospitalization
          // If they had hospitalization, they pass regardless of medication
          return allAnswers.severe_exacerbation === "no";
        }
      },
      {
        value: "yes_twice_or_more",
        label: "Yes, I have required prescribed medication twice or more"
      },
    ],
    disqualifyMessage: "This study requires participants who have either been hospitalized for a COPD flare-up OR required prescribed medication to treat COPD flare-ups at least twice in the past 12 months.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_COPD_EXACERBATION_MODERATE_2_12_MONTHS, booleanAttributeValue: false },
      ],
      yes_once: [
        { attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_COPD_EXACERBATION_MODERATE_2_12_MONTHS, booleanAttributeValue: false },
      ],
      yes_twice_or_more: [
        { attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_COPD_EXACERBATION_MODERATE_2_12_MONTHS, booleanAttributeValue: true },
      ],
    }),
  },
  {
    id: "maintenance_therapy",
    type: QuestionType.RADIO,
    question: "Are you currently using a 'maintenance' inhaler every day to manage your COPD?",
    helpText: "Maintenance inhalers are used every day to prevent COPD flare ups, and reliever inhalers are used to treat symptoms when they occur.",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes, I use a maintenance inhaler every day" },
      {
        value: "no",
        label: "No, I only use a reliever inhaler as necessary",
        disqualify: true
      },
    ],
    disqualifyMessage: "This study requires participants who are currently using a maintenance inhaler every day to manage COPD symptoms.",
    attributeMapping: mappers.value({
      yes: [],
      no: [],
    }),
  },
  {
    id: "inhaler_combination",
    type: QuestionType.RADIO,
    question: "Do your COPD inhaled medications consist of one medication ingredient, two medication ingredients, or three medication ingredients?",
    helpText: "If you're not sure, check the label on your inhaler/s or your prescription information. The medication ingredients may be combined all in one device or divided between two inhaler devices.",
    required: true,
    allowBack: true,
    options: [
      { value: "single", label: "One medication ingredient", disqualify: true },
      { value: "dual", label: "Two medication ingredients" },
      { value: "triple", label: "Three medication ingredients" },
      { value: "not_sure", label: "Not sure" },
    ],
    disqualifyMessage: "This study requires participants who are using a dual or triple combination inhaler therapy.",
    attributeMapping: mappers.value({
      dual: [
        { attributeDefinitionId: 26423, booleanAttributeValue: true }, // currently_using_dual_or_triple_inhaler_therapy
      ],
      triple: [
        { attributeDefinitionId: 26423, booleanAttributeValue: true }, // currently_using_dual_or_triple_inhaler_therapy
      ],
      single: [
        { attributeDefinitionId: 26423, booleanAttributeValue: false }, // currently_using_dual_or_triple_inhaler_therapy
      ],
      not_sure: [], // skipped - can follow up via AI SMS
    }),
  },
  {
    ...standardHeightQuestion,
    attributeMapping: mappers.height({
      attributeDefinitionId: MEASUREMENT_ATTRIBUTES.HEIGHT,
    }),
  },
  {
    ...standardWeightQuestion,
    minBMI: 18,
    maxBMI: 44.9,
    helpText: "We'll calculate your BMI (Body Mass Index) using your height and weight.",
    attributeMapping: mappers.weight({
      attributeDefinitionId: MEASUREMENT_ATTRIBUTES.WEIGHT,
    }),
  },
  {
    id: "respiratory_conditions",
    type: QuestionType.CHECKBOX,
    question: "Have you experienced or been diagnosed with any of the following respiratory conditions?",
    helpText: "Please select all that apply. If none apply, select 'None of the above'.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "asthma",
        label: "Asthma (not including childhood asthma)",
        disqualify: true
      },
      {
        value: "bronchiectasis",
        label: "Bronchiectasis",
        disqualify: true
      },
      {
        value: "interstitial_lung_disease",
        label: "Interstitial lung disease (ILD) or lung fibrosis",
        disqualify: true
      },
      {
        value: "cystic_fibrosis",
        label: "Cystic fibrosis (CF)",
        disqualify: true
      },
      {
        value: "alpha1_antitrypsin",
        label: "Alpha-1 antitrypsin deficiency",
        disqualify: true
      },
      {
        value: "primary_ciliary_dyskinesia",
        label: "Primary ciliary dyskinesia",
        disqualify: true
      },
      {
        value: "lung_resection",
        label: "Surgical removal of lung tissue (lung resection)",
        disqualify: true
      },
      {
        value: "high_oxygen_therapy",
        label: "Oxygen therapy with concentrator setting of 4 or higher",
        disqualify: true
      },
      {
        value: "none",
        label: "None of the above"
      },
    ],
    disqualifyMessage: "This study cannot include participants with these respiratory conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        asthma: [
          { attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_ASTHMA, booleanAttributeValue: true },
        ],
        bronchiectasis: [
          { attributeDefinitionId: 24226, booleanAttributeValue: true },
        ],
        interstitial_lung_disease: [
          { attributeDefinitionId: 25661, booleanAttributeValue: true },
        ],
        cystic_fibrosis: [],
        alpha1_antitrypsin: [],
        primary_ciliary_dyskinesia: [],
        lung_resection: [],
        high_oxygen_therapy: [],
      },
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_ASTHMA, booleanAttributeValue: false },
          { attributeDefinitionId: 24226, booleanAttributeValue: false },
          { attributeDefinitionId: 25661, booleanAttributeValue: false },
        ],
      },
    }),
  },
  {
    id: "cardiovascular_conditions",
    type: QuestionType.CHECKBOX,
    question: "Have you experienced or been diagnosed with any of the following cardiovascular (heart/blood vessel) conditions?",
    helpText: "Please select all that apply. If none apply, select 'None of the above'.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "heart_failure",
        label: "Severe heart failure",
        disqualify: true
      },
      {
        value: "heart_attack_recent",
        label: "Heart attack (myocardial infarction) in the past 6 months",
        disqualify: true
      },
      {
        value: "stroke_recent",
        label: "Stroke, mini-stroke, or transient ischemic attack (TIA) in the past 6 months",
        disqualify: true
      },
      {
        value: "angina_hospitalization",
        label: "Hospitalization due to angina or heart failure in the past 6 months",
        disqualify: true
      },
      {
        value: "coronary_revascularization",
        label: "Coronary artery disease or history of coronary revascularization (e.g., stent, bypass) in the past 6 months",
        disqualify: true
      },
      {
        value: "cardiomyopathy",
        label: "Cardiomyopathy (heart muscle disease)",
        disqualify: true
      },
      {
        value: "pulmonary_hypertension",
        label: "Pulmonary hypertension (high blood pressure in the lungs)",
        disqualify: true
      },
      {
        value: "none",
        label: "None of the above"
      },
    ],
    disqualifyMessage: "This study cannot include participants with these cardiovascular conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        heart_failure: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: true },
        ],
        heart_attack_recent: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: true },
        ],
        stroke_recent: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STROKE_TIA, booleanAttributeValue: true },
        ],
        angina_hospitalization: [],
        coronary_revascularization: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CORONARY_ARTERY_REVASCULARIZATION, booleanAttributeValue: true },
        ],
        cardiomyopathy: [
          { attributeDefinitionId: 23518, booleanAttributeValue: true },
        ],
        pulmonary_hypertension: [
          { attributeDefinitionId: 8249, booleanAttributeValue: true },
        ],
      },
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STROKE_TIA, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CORONARY_ARTERY_REVASCULARIZATION, booleanAttributeValue: false },
          { attributeDefinitionId: 23518, booleanAttributeValue: false },
          { attributeDefinitionId: 8249, booleanAttributeValue: false },
        ],
      },
    }),
  },
  {
    id: "exclusionary_conditions",
    type: QuestionType.CHECKBOX,
    question: "Have you been diagnosed with any of the following conditions?",
    helpText: "Please select all that apply. If none apply, select 'None of the above'.",
    required: true,
    allowBack: true,
    options: [
      { value: "ulcerative_colitis", label: "Ulcerative colitis (UC)", disqualify: true },
      { value: "crohns_disease", label: "Crohn's disease", disqualify: true },
      { value: "liver_failure", label: "Liver failure or cirrhosis", disqualify: true },
      { value: "eskd", label: "End-stage kidney disease (ESKD) or currently on dialysis", disqualify: true },
      { value: "hepatitis", label: "Hepatitis (B or C)", disqualify: true },
      { value: "hiv", label: "HIV", disqualify: true },
      { value: "active_tb", label: "Active tuberculosis (TB) or currently on TB treatment", disqualify: true },
      { value: "dementia", label: "Dementia (formally diagnosed)", disqualify: true },
      { value: "none", label: "None of the above" },
    ],
    disqualifyMessage: "This study cannot include participants with these conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        ulcerative_colitis: [
          { attributeDefinitionId: GASTROINTESTINAL_ATTRIBUTES.HAS_ULCERATIVE_COLITIS, booleanAttributeValue: true },
        ],
        crohns_disease: [
          { attributeDefinitionId: GASTROINTESTINAL_ATTRIBUTES.HAS_CROHNS, booleanAttributeValue: true },
        ],
        liver_failure: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: true },
        ],
        eskd: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: true },
        ],
        hepatitis: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_B, booleanAttributeValue: true },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_C, booleanAttributeValue: true },
        ],
        hiv: [
          { attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_HIV, booleanAttributeValue: true },
        ],
        active_tb: [
          { attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_TUBERCULOSIS, booleanAttributeValue: true },
        ],
        dementia: [
          { attributeDefinitionId: 23896, booleanAttributeValue: true },
        ],
      },
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: GASTROINTESTINAL_ATTRIBUTES.HAS_ULCERATIVE_COLITIS, booleanAttributeValue: false },
          { attributeDefinitionId: GASTROINTESTINAL_ATTRIBUTES.HAS_CROHNS, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_B, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_HEPATITIS_C, booleanAttributeValue: false },
          { attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_HIV, booleanAttributeValue: false },
          { attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_TUBERCULOSIS, booleanAttributeValue: false },
          { attributeDefinitionId: 23896, booleanAttributeValue: false },
        ],
      },
    }),
  },
  {
    id: "immunosuppression_transplant",
    type: QuestionType.CHECKBOX,
    question: "Do any of these apply to you?",
    helpText: "Please select all that apply. If none apply, select 'None of the above'.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "organ_transplant",
        label: "I have a history of organ transplant or I am currently on the transplant waitlist",
        disqualify: true
      },
      {
        value: "immunosuppressive_medications",
        label: "I take immunosuppressant medications",
        disqualify: true
      },
      {
        value: "cancer_recent",
        label: "I have had cancer within the past 5 years (excluding non-melanoma skin cancers such as basal cell or squamous cell carcinoma)",
        disqualify: true
      },
      {
        value: "none",
        label: "None of the above"
      },
    ],
    disqualifyMessage: "This study cannot include participants with these conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        organ_transplant: [
          { attributeDefinitionId: 15039, booleanAttributeValue: true },
        ],
        immunosuppressive_medications: [],
        cancer_recent: [],
      },
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: 15039, booleanAttributeValue: false },
        ],
      },
    }),
  },
  {
    id: "concurrent_trial",
    type: QuestionType.RADIO,
    question: "Are you currently participating in another clinical trial?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no",
        label: "No, I am not currently participating in another clinical trial"
      },
      {
        value: "yes",
        label: "Yes, I am currently participating in another clinical trial",
        disqualify: true
      },
    ],
    disqualifyMessage: "For safety reasons, participants currently enrolled in another clinical trial cannot participate in this study.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
      yes: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true },
      ],
    }),
  },
];
